import { getCurrentViewToken, getCurrentViewStartPerfNow } from './datadogRum';

// Explicit call sites only. Never accept store values, query keys, user IDs or
// arbitrary strings. These are nearby updates, not proof of a suspension's cause.
export type EventPageHydrationUpdate =
    | 'auth_restored_signed_in'
    | 'auth_restored_signed_out'
    | 'auth_user_updated'
    | 'statsig_snapshot_scheduled'
    | 'event_shell_committed'
    | 'entity_seed_started'
    | 'entity_seed_finished';

const MAX_UPDATES = 32;
const LOOKBACK_MS = 1_000;
const CAPTURE_WINDOW_MS = 60_000;
interface Update {
    name: EventPageHydrationUpdate;
    at: number;
}
let scope: { pathname: string; token: number; startedAt: number; updates: Update[]; dropped: number } | undefined;

/** Owned by the pre-hydration flash observer, including route changes/teardown. */
export function startEventPageHydrationTrace(pathname: string): void {
    scope = {
        pathname,
        token: getCurrentViewToken(),
        startedAt: performance.now(),
        updates: [],
        dropped: 0,
    };
}

export function stopEventPageHydrationTrace(): void {
    scope = undefined;
}

function currentScope() {
    if (!scope || typeof window === 'undefined') return undefined;
    return scope.pathname === window.location.pathname && scope.token === getCurrentViewToken() ? scope : undefined;
}

export function recordEventPageHydrationUpdate(name: EventPageHydrationUpdate): void {
    const trace = currentScope();
    if (!trace) return;
    const at = performance.now();
    if (at - trace.startedAt > CAPTURE_WINDOW_MS || document.visibilityState !== 'visible') return;
    if (trace.updates.length === MAX_UPDATES) {
        trace.updates.shift();
        trace.dropped++;
    }
    trace.updates.push({ name, at });
}

/** Bounded diagnostics attached to the existing flash vital; no extra RUM events. */
export function getEventPageFlashDiagnostics(start: number, end: number): Record<string, unknown> {
    const trace = currentScope();
    if (!trace) return {};
    const recent = trace.updates.filter((update) => update.at >= start - LOOKBACK_MS && update.at <= end);
    const preceding = recent.findLast((update) => update.at <= start);
    const result: Record<string, unknown> = {
        schema_version: 1,
        clock: 'performance',
        wall_duration_ms: Math.max(0, Math.round(end - start)),
        update_capture_expired: end - trace.startedAt > CAPTURE_WINDOW_MS,
        updates_dropped: trace.dropped,
        recent_updates: recent.map(({ name, at }) => ({ name, offset_ms: Math.round(at - start) })),
        ...(preceding && {
            last_update_before_onset: preceding.name,
            last_update_before_onset_age_ms: Math.round(start - preceding.at),
        }),
    };
    // Navigation Timing describes this document, not subsequent SPA views. A zero
    // responseEnd means the stream has not finished; don't turn it into a long tail.
    if (typeof performance.getEntriesByType !== 'function') return result;
    if (getCurrentViewStartPerfNow() === 0) {
        const navigation = performance.getEntriesByType('navigation')[0] as PerformanceNavigationTiming | undefined;
        const responseEnd = navigation?.responseEnd;
        if (navigation) result.document_response_complete = !!responseEnd && responseEnd <= end;
        if (responseEnd && responseEnd <= end) {
            result.document_response_end_offset_ms = Math.round(responseEnd - start);
            result.after_document_response_ms = Math.round(end - Math.max(start, responseEnd));
        }
    }
    // Resource entries exist only after completion. Report overlapping Next script
    // downloads as correlation, never as outstanding requests or execution time.
    const scripts = performance.getEntriesByType('resource').filter((entry) => {
        const resource = entry as PerformanceResourceTiming;
        if (resource.responseEnd <= start || resource.responseEnd > end) return false;
        try {
            const url = new URL(resource.name);
            return (
                url.origin === location.origin &&
                url.pathname.startsWith('/_next/static/') &&
                url.pathname.endsWith('.js')
            );
        } catch {
            return false;
        }
    }) as PerformanceResourceTiming[];
    result.next_script_completions = scripts.length;
    const latestScript = scripts.reduce<PerformanceResourceTiming | undefined>(
        (latest, script) => (!latest || script.responseEnd > latest.responseEnd ? script : latest),
        undefined,
    );
    if (latestScript) {
        result.last_next_script = new URL(latestScript.name).pathname.slice(-160);
        result.last_next_script_end_offset_ms = Math.round(latestScript.responseEnd - start);
    }
    return result;
}
